import { Language } from "@mindlercare/schema";
import Checkbox from "components/lib/checkbox";
import { Typography } from "components/typography";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useMindlerApi } from "../../api/apiv2";

type LanguagesProps = {
  hasError: boolean;
  selectedLanguages: number[];
  onChange: (languageIds: number[]) => void;
};

export const Languages = ({
  hasError,
  selectedLanguages = [],
  onChange,
}: PropsWithChildren<LanguagesProps>) => {
  const { t } = useTranslation();
  const mindlerApi = useMindlerApi();
  const { data, isLoading, isSuccess } = useQuery(
    mindlerApi.languages.queryOptions(),
  );

  if (isLoading || !isSuccess) return null;

  const sortedLanguages = data
    .map((x) => {
      const upperCaseName = x.codeISO3.toUpperCase();
      const languageName = t(upperCaseName, "*Unknown*");

      return { ...x, name: languageName };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <LanguagesContainer>
      <Typography
        variant="h3"
        color={hasError ? "ERROR" : undefined}
        insensitive
      >
        {`${t("SPOKEN_LANGUAGE")}*`}
      </Typography>
      <div>
        {sortedLanguages.map(({ name, languageId }) => (
          <LanguageItem
            key={name}
            name={name}
            languageId={languageId}
            isSelected={selectedLanguages.includes(languageId)}
            onSelectedChange={(languageId) => {
              if (selectedLanguages.includes(languageId)) {
                onChange(selectedLanguages.filter((id) => id !== languageId));
              } else {
                onChange([...selectedLanguages, languageId]);
              }
            }}
          />
        ))}
      </div>
    </LanguagesContainer>
  );
};

type LanguageProps = {
  isSelected: boolean;
  onSelectedChange: (languageId: number) => void;
} & Pick<Language, "languageId" | "name">;

const LanguageItem = ({
  languageId,
  name,
  isSelected,
  onSelectedChange,
}: LanguageProps) => (
  <LanguageItemContainer>
    <Typography variant="body" insensitive>
      {name}
    </Typography>
    <Checkbox
      value={name}
      checked={isSelected}
      onChange={() => onSelectedChange(languageId)}
    />
  </LanguageItemContainer>
);

const LanguagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  & > div {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 32px;
  }
`;

const LanguageItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ceccc4;

  & p {
    margin: 16px 0;
  }
`;
